import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Plus } from 'lucide-react';
import { Button, EmptyState, useToast } from '@lognai/synapse-design-system-v2';
import {
  STATUS_LABELS,
  TASK_STAGE_THEME,
  TASK_STATUSES,
  TaskCard,
  TaskForm,
  useAssignableUsers,
  useCreateTask,
  useTasks,
  useUpdateTaskStatus,
  type CreateTaskDto,
  type Task,
  type TaskStatus,
} from '@lognai/plugin-tasks-ui';

/**
 * Width at which every stage fits side by side. Below it the card pages through
 * one stage at a time.
 *
 * Measured from the card body rather than derived from the workspace grid: a
 * card can be resized to any width at any breakpoint, so the column count is
 * not a reliable proxy for how much room this content actually has.
 *
 * Three lanes of task cards need roughly this much room to stay legible. At the
 * default 2-column span (~400px inner) the card therefore starts paged, and
 * widening it past this threshold switches it to all three lanes at once.
 */
const ALL_STAGES_MIN_WIDTH = 520;

function useCardWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const element = ref.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

interface StagePaging {
  onPrev: () => void;
  onNext: () => void;
  canPrev: boolean;
  canNext: boolean;
}

/**
 * The lane header, wearing the collaboration module's own stage colours so a
 * glance reads To Do / In Progress / Done the same way the Task Board does.
 *
 * `px-4` matches the `p-4` inside a TaskCard, so the stage name lines up with
 * the task text beneath it and with the card title above. The board's leading
 * status dot is dropped at this size: it repeated what the tinted band and the
 * tinted label already say, and its 16px of lead was what pushed the label out
 * of alignment with everything else in the card.
 *
 * In the paged view the arrows live inside the band, grouped with the count, so
 * the label keeps exactly the same inset in both layouts.
 */
function StageHeader({
  status,
  count,
  paging,
}: {
  status: TaskStatus;
  count: number;
  paging?: StagePaging;
}) {
  const theme = TASK_STAGE_THEME[status];
  return (
    <div className={`mb-2 flex h-9 shrink-0 items-center gap-2 rounded-md px-4 ${theme.header}`}>
      <span className={`truncate text-xs font-semibold uppercase tracking-[0.08em] ${theme.title}`}>
        {STATUS_LABELS[status]}
      </span>
      <span
        className={`ml-auto inline-flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full px-1.5 text-[11px] font-semibold tabular-nums ${
          count === 0 ? 'bg-muted text-muted-foreground' : theme.count
        }`}
      >
        {count}
      </span>
      {paging && (
        <span className="-mr-2 flex shrink-0 items-center">
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 px-0"
            aria-label="Previous stage"
            disabled={!paging.canPrev}
            onClick={paging.onPrev}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 px-0"
            aria-label="Next stage"
            disabled={!paging.canNext}
            onClick={paging.onNext}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </span>
      )}
    </div>
  );
}

function StageColumn({
  status,
  tasks,
  onStatusChange,
  updatingId,
}: {
  status: TaskStatus;
  tasks: Task[];
  onStatusChange: (id: string, next: TaskStatus) => void;
  updatingId: string | null;
}) {
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <StageHeader status={status} count={tasks.length} />
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
        {tasks.length === 0 ? (
          <p className="px-1 py-6 text-center text-xs text-muted-foreground">Nothing here</p>
        ) : (
          tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onStatusChange={onStatusChange}
              isUpdating={updatingId === task.id}
              canChangeStatus
            />
          ))
        )}
      </div>
    </div>
  );
}

/**
 * "My work" on the workspace: the caller's own tasks grouped by stage, with the
 * collaboration module's own create-task modal so a task raised here is
 * identical to one raised on the board.
 */
export function CollaborationCard() {
  const { ref, width } = useCardWidth<HTMLDivElement>();
  const { toast } = useToast();

  const { data, isLoading, error } = useTasks({ mine: true, limit: 50 });
  const { data: assignableUsers = [] } = useAssignableUsers();
  const createTask = useCreateTask();
  const updateStatus = useUpdateTaskStatus();

  const [formOpen, setFormOpen] = useState(false);
  const [stageIndex, setStageIndex] = useState(0);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const tasks = data?.items ?? [];
  const byStage = useMemo(() => {
    const grouped = new Map<TaskStatus, Task[]>(TASK_STATUSES.map((status) => [status, []]));
    for (const task of tasks) grouped.get(task.status)?.push(task);
    return grouped;
  }, [tasks]);

  // `width === 0` is the first paint before the observer reports; assume the
  // roomy layout so the card does not flash the paged view.
  const showAllStages = width === 0 || width >= ALL_STAGES_MIN_WIDTH;
  const activeStage = TASK_STATUSES[Math.min(stageIndex, TASK_STATUSES.length - 1)];

  // In the paged view, open on a stage that actually holds work. Defaulting to
  // "To Do" hides the user's only task behind an arrow when it sits elsewhere.
  const firstPopulatedStage = TASK_STATUSES.findIndex(
    (status) => (byStage.get(status) ?? []).length > 0
  );
  const landedOnStage = useRef(false);
  useEffect(() => {
    if (landedOnStage.current || showAllStages || firstPopulatedStage < 0) return;
    landedOnStage.current = true;
    setStageIndex(firstPopulatedStage);
  }, [firstPopulatedStage, showAllStages]);

  const changeStatus = async (id: string, next: TaskStatus) => {
    setUpdatingId(id);
    try {
      await updateStatus.mutateAsync({ id, status: next });
    } catch {
      toast({ title: 'Could not update the task', variant: 'destructive' });
    } finally {
      setUpdatingId(null);
    }
  };

  const submit = async (values: CreateTaskDto) => {
    try {
      await createTask.mutateAsync(values);
      toast({ title: 'Task created' });
      setFormOpen(false);
    } catch {
      toast({ title: 'Could not create the task', variant: 'destructive' });
    }
  };

  return (
    // `h-full`, not `flex-1`: the card shell renders its body into a plain block
    // wrapper, so `flex-1` would collapse to the content's own height and the
    // Create Task footer would float up under a short list instead of sitting at
    // the bottom of the card.
    <div ref={ref} className="flex h-full min-h-0 flex-col px-4 pb-3">
      <div className="flex min-h-0 flex-1 flex-col">
        {isLoading ? (
          <div className="flex min-h-32 flex-1 items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex min-h-32 flex-1 items-center justify-center px-6 text-center">
            <p className="text-xs text-muted-foreground">Your tasks could not be loaded.</p>
          </div>
        ) : tasks.length === 0 ? (
          <div className="flex min-h-0 flex-1 items-center justify-center">
            <EmptyState
              compact
              title="No tasks assigned to you"
              description="Tasks assigned to you appear here, grouped by stage."
            />
          </div>
        ) : showAllStages ? (
          <div className="flex min-h-0 flex-1 gap-3">
            {TASK_STATUSES.map((status) => (
              <StageColumn
                key={status}
                status={status}
                tasks={byStage.get(status) ?? []}
                onStatusChange={changeStatus}
                updatingId={updatingId}
              />
            ))}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <StageHeader
              status={activeStage}
              count={(byStage.get(activeStage) ?? []).length}
              paging={{
                onPrev: () => setStageIndex((index) => Math.max(0, index - 1)),
                onNext: () =>
                  setStageIndex((index) => Math.min(TASK_STATUSES.length - 1, index + 1)),
                canPrev: stageIndex > 0,
                canNext: stageIndex < TASK_STATUSES.length - 1,
              }}
            />
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
              {(byStage.get(activeStage) ?? []).length === 0 ? (
                <p className="px-1 py-6 text-center text-xs text-muted-foreground">Nothing here</p>
              ) : (
                (byStage.get(activeStage) ?? []).map((task) => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onStatusChange={changeStatus}
                    isUpdating={updatingId === task.id}
                    canChangeStatus
                  />
                ))
              )}
            </div>
          </div>
        )}
      </div>

      <div className="mt-3 shrink-0 border-t border-border pt-3">
        {/* The card's one call to action, so it carries the primary fill; the
            per-task stage buttons are deliberately neutral beside it. */}
        <Button variant="primary" size="sm" className="w-full" onClick={() => setFormOpen(true)}>
          <Plus className="h-4 w-4" />
          Create Task
        </Button>
      </div>

      {/* The collaboration module's own modal — same fields and validation as
          the Task Board, so nothing about task creation forks here. */}
      <TaskForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onSubmit={submit}
        isLoading={createTask.isPending}
        assignableUsers={assignableUsers}
      />
    </div>
  );
}
